<script lang="ts">
  import { onMount } from "svelte";
  import videojs from "video.js";
  import { fade } from "svelte/transition";
  import type { VideoSource } from "./types";
  import type Player from "video.js/dist/types/player";
  import { Cloudinary } from "@cloudinary/url-gen";

  const cld = new Cloudinary({
    cloud: {
      cloudName: "belstaff",
    },
  });

  const createId = (length: number) => {
    var result = "";
    var characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };

  export let id = `id-${createId(50)}`;

  export let sources: VideoSource[] = [];
  export let poster: string;
  export let preload: string = "auto";
  export let controls: boolean = true;
  export let autoplay: boolean = false;
  export let muted: boolean = true;
  export let loop: boolean = false;
  export let fluid: boolean = true;
  export let fill: boolean = false;
  export let inactivityTimeout: number = 2500;
  export let videoShouldPlay: boolean = false;

  let player: any;
  let titleEl: HTMLElement | null;
  let fullScreenEl: HTMLElement | null;
  let showTitle = true;
  let showFullScreenIcon = true;
  let playerReady = false;

  const isPlaying = (player: Player) => {
    return player.currentTime() > 0 && !player.paused() && !player.ended();
  };

  const getIdFromSource = (source: string) => {
    const parts = source.split("/");
    const lastPart = parts[parts.length - 1];
    return lastPart.split(".")[0];
  };

  const videoId = getIdFromSource(sources[0].src);

  poster = poster || cld.image(videoId).format("auto").quality("auto").toURL();

  // Handle the case where the video source is an HLS stream
  // and the player encounters an error
  // This is a workaround for the issue where HLS streams
  // are not available straight away from Cloudinary
  $: sources = [
    ...sources,
    {
      src: cld.video(getIdFromSource(sources[0].src)).toURL(),
      type: "video/mp4",
      poster,
    },
  ];

  const togglePlay = () => {
    if (isPlaying(player)) {
      player.pause();
      showTitle = true;
    } else {
      player.play();
      showTitle = false;
    }
  };

  const toggleFullScreen = () => {
    let isFullScreen = player.isFullscreen();
    isFullScreen ? player.exitFullscreen() : player.requestFullscreen();
  };

  const hideTitle = () => {
    showTitle = false;
  };

  const videoPlay = () => {
    if (player && !isPlaying(player)) {
      player.play();
    }
  };

  const videoPause = () => {
    if (player && isPlaying(player)) {
      player.pause();
    }
  };

  const videoReset = () => {
    if (player) {
      player.pause();
      player.currentTime(0);
      player.hasStarted(false);
    }
  };

  $: videoShouldPlay ? videoPlay() : videoPause();

  onMount(() => {
    player = videojs(
      id,
      {
        controls: controls,
        autoplay: autoplay ? "muted" : false,
        muted: muted,
        playsinline: autoplay,
        loop: loop,
        fluid: fluid,
        fill: fill,
        poster: poster,
        sources: sources,
        preload: preload,
        inactivityTimeout: inactivityTimeout,
        html5: {
          vhs: {
            useDevicePixelRatio: true,
            bandwidth: 10000000,
          },
        },
      },
      null,
    );

    player.on("click", evt => {
      if (
        evt.target.tagName === "VIDEO" ||
        evt.target.tagName === "IMG" ||
        evt.target.classList.contains("vjs-poster") ||
        evt.target.classList.contains("vjs-play-control") ||
        evt.target.classList.contains("vjs-icon-placeholder") ||
        evt.target.parentElement.classList.contains("vjs-play-control")
      ) {
        hideTitle();
      }
    });

    // Mark player as ready when video.js is initialized
    player.ready(() => {
      playerReady = true;
    });
  });

  $: sources.forEach((source, i) => {
    if (source.src) {
      if (source.src.includes("flite")) {
        source.type = "video/mp4";
        sources[i] = source;
      } else {
        const format = source.src.split(".").pop();
        if (format === "mp4") {
          source.type = "video/mp4";
        } else if (format === "webm") {
          source.type = "video/webm";
        } else if (format === "ogg") {
          source.type = "video/ogg";
        } else if (format === "m3u8") {
          source.type = "application/x-mpegURL";
        } else if (format === "flv") {
          source.type = "video/x-flv";
        } else if (format === "avi") {
          source.type = "video/x-msvideo";
        }
      }
    }
  });

  $: {
    if (sources && player) player.src(sources);
  }
</script>

<style lang="scss">
  :global(.vjs-fill .vjs-tech) {
    object-fit: cover;
    object-position: center;
  }
  :global(.vjs-fill .vjs-poster img) {
    object-fit: cover;
    object-position: center;
  }

  .hidden {
    display: none;
  }

  .video-poster-wrapper {
    position: relative;
    width: 100%;
    height: 100%;

    img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }
  }

  :global(video-js) {
    transition: opacity 0.3s ease-in-out;
  }

  .video-container {
    position: relative;
    width: 100%;
    height: 100% !important;
  }
</style>

<svelte:head>
  <link rel="stylesheet" href="https://vjs.zencdn.net/8.0.4/video-js.css" />
</svelte:head>

<div class="video-container">
  <!-- Render poster image immediately for better LCP -->
  {#if poster && !playerReady}
    <div class="video-poster-wrapper">
      <img src={poster} alt="" loading="eager" />
    </div>
  {/if}

  <video-js {id} />
</div>
<div
  tabindex="0"
  bind:this={titleEl}
  on:click={togglePlay}
  on:click={hideTitle}
  on:keydown={togglePlay}
  role="button"
  class:hidden={showTitle}
>
  <slot name="title" />
</div>

<div
  class:hidden={showFullScreenIcon}
  tabindex="0"
  transition:fade
  bind:this={fullScreenEl}
  on:click={toggleFullScreen}
  on:keydown
  role="button"
>
  <slot name="fullscreen" />
</div>
